import React from 'react';
import PropTypes from "prop-types";
import makeStyles from '@mui/styles/makeStyles';
import AppBar from "@mui/material/AppBar";
import Divider from '@mui/material/Divider';
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import ProfilePlaceholder from '../../assets/Images/ProfilePlaceholder.png';
import Menu from '@mui/material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import { Link, Button, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsBar from "../Notifications/NotificationsBar";

const useStyles = makeStyles((theme) => ({
    appBar: {
        [theme.breakpoints.up('sm')]: {
            width: '100%',
        },
        zIndex: theme.zIndex.drawer + 1,
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
        marginTop: 15,
        fontSize: 20,
        fontStyle: 'italic',
        color: '#707070',
        fontWeight: 600,
        marginLeft: 10,
        whiteSpace: 'pre-wrap',
        lineHeight: 'normal',
        fontFamily: 'Gill Sans MT',
    },
    vline: {
        borderLeft: '1px solid #00000029',
        height: 50,
        marginLeft: 50
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    notificationBadge: {
        backgroundColor: "#f44336",
        color: "white",
    },

}));

const NavBar = props => {
    const { logo, isAuthorize, userName, isOpenMenu, setOpenMenu, isDisabled } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isNotiDrawerOpen, setNotiDrawerOpen] = React.useState(false);
    const [notiCounter, setNotiCounter] = React.useState("");
    const [notiData, setNotiData] = React.useState([]);
    const [avatarUrl, setAvatarUrl] = React.useState(null);
    const open = Boolean(anchorEl);
    const classes = useStyles();

    const notiCallTimeMiliSec = 30000; // 30 seconds

    const fetchFileAsDataUrl = async (filePathWithName) => {
        const url = `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/CommonFileViewByFullPath?filePathWithName=${encodeURIComponent(filePathWithName)}`;
        const res = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: "Bearer " + localStorage.getItem("lmsToken"),
            },
        });
        if (!res.ok) {
            if (res.status === 401) throw new Error("Unauthorized");
            return null;
        }
        const blob = await res.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const onLogout = () => {
        window.localStorage.removeItem("lmsData");
        window.localStorage.removeItem("lmsToken");
        window.localStorage.removeItem("lmsStudentId");
        window.localStorage.removeItem("lmsMeetingConfig");
        sessionStorage.removeItem("lmsFeeAnnouncementReadFlag");
        sessionStorage.removeItem("lmsAnnouncementReadFlag");
        window.location.replace("#/login");
    }
    const homepage = () => {
        window.location.replace("#/dashboard");
    }

    const getCounterData = async (forceUpdate = false) => {
        // Don't update counter if drawer is open (unless forced)
        if (!forceUpdate && isNotiDrawerOpen) {
            return;
        }

        const endpoint = "/notifications/C00CommonStudentsNotificationsForWebCountView";
        const url = `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}${endpoint}`;

        try {
            const res = await fetch(url, {
                method: "GET",
                headers: new Headers({
                    Authorization: "Bearer " + localStorage.getItem("lmsToken"),
                }),
            });

            if (!res.ok) throw res;

            const json = await res.json();
            if (json.CODE === 1) {
                let data = json.DATA || [];
                if (data.length > 0) {
                    const { unReadCountLabel, unReadCount } = data[0] || {};
                    // Only set counter if there are actually unread notifications
                    setNotiCounter(unReadCount > 0 ? unReadCountLabel : "");
                }
            }
        } catch (error) {
            console.error("Error fetching notification count:", error);
        }
    };

    const getAndUpdateAllNotificationsStatus = async () => {
        const endpoint = "/notifications/C00CommonStudentsNotificationsForWebView";
        const url = `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}${endpoint}`;

        try {
            const res = await fetch(url, {
                method: "GET",
                headers: new Headers({
                    Authorization: "Bearer " + localStorage.getItem("lmsToken"),
                }),
            });

            if (!res.ok) throw res;

            const json = await res.json();
            if (json.CODE === 1) {
                let data = json.DATA || [];
                setNotiData(data);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    };

    const handleNotiDrawerToggle = async (e) => {
        e.preventDefault();
        const value = !isNotiDrawerOpen;
        if (value) {
            // Opening drawer: Reset counter immediately
            setNotiCounter("");
            await getAndUpdateAllNotificationsStatus();
        } else {
            // Closing drawer: Refresh counter from server
            setTimeout(() => {
                getCounterData(true); // Force update after closing
            }, 300);
        }
        setNotiDrawerOpen(value);
    };

    React.useEffect(() => {
        let isMounted = true;
        const loadAvatar = async () => {
            try {
                if (!isAuthorize) return;
                const lmsData = localStorage.getItem("lmsData") ? JSON.parse(localStorage.getItem("lmsData")) : {};
                if (lmsData.profilePhotoUrl) {
                    const dataUrl = await fetchFileAsDataUrl(lmsData.profilePhotoUrl);
                    if (dataUrl && isMounted) setAvatarUrl(dataUrl);
                }
            } catch (err) {
                console.error("Error loading avatar:", err);
            }
        };
        loadAvatar();
        return () => { isMounted = false; };
    }, [isAuthorize]);

    React.useEffect(() => {
        let interval;
        if (isAuthorize) {
            getCounterData();
            interval = setInterval(function () {
                getCounterData();
            }, notiCallTimeMiliSec);
        }
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isAuthorize]);

    const lmsData = localStorage.getItem("lmsData") ? JSON.parse(localStorage.getItem("lmsData")) : {};

    return (
        <div id="headerUclLms">
            <AppBar style={{ color: 'black', backgroundColor: "white" }} position="fixed" className={classes.appBar}>
                <Toolbar style={{
                    paddingLeft: 3, display: 'flex',
                    justifyContent: 'space-between',
                    minHeight: `${isAuthorize ? '' : '57px'}`
                }} variant="dense">
                    <div style={{ display: 'flex' }}>
                        {isAuthorize &&
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                style={{ marginLeft: 5 }}
                                title={isOpenMenu ? 'Hide Menu' : 'Show Menu'}
                                onClick={(e) => setOpenMenu(e)}
                                disabled={isDisabled}
                                size="large">
                                {isOpenMenu ? <ArrowBackIosIcon /> : <MenuIcon />}
                            </IconButton>
                        }

                        <IconButton style={{ padding: 5 }} color="inherit" aria-label="Menu" size="large">
                            {logo && <img alt="" src={logo} width={50} />}
                        </IconButton>
                        <Typography className={classes.title} variant="subtitle1" noWrap>
                            {"Universal College Lahore"}
                        </Typography>
                    </div>
                    {isAuthorize && (
                        <div>
                            <IconButton
                                onClick={homepage}
                                title="Go to home page"
                                color="primary"
                                disabled={isDisabled}
                                style={{ opacity: '0.8' }}
                                size="large">
                                <HomeIcon />
                            </IconButton>

                            <IconButton
                                onClick={(e) => {
                                    handleNotiDrawerToggle(e);
                                }}
                                title="Notifications"
                                color="primary"
                                style={{ opacity: '0.8' }}
                                size="large">
                                {notiCounter && !isNotiDrawerOpen ? (
                                    <Badge
                                        color="error"
                                        overlap="rectangular"
                                        badgeContent={notiCounter}
                                        classes={{
                                            badge: classes.notificationBadge
                                        }}
                                    >
                                        <NotificationsIcon />
                                    </Badge>
                                ) : (
                                    isNotiDrawerOpen ? (
                                        <NotificationsActiveIcon />
                                    ) : (
                                        <NotificationsIcon />
                                    )
                                )}
                            </IconButton>

                            <IconButton
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                color="inherit"
                                size="large">
                                <img
                                    alt=""
                                    style={{ borderRadius: '50%' }}
                                    src={avatarUrl || ProfilePlaceholder}
                                    width={30}
                                />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={open}
                                onClose={handleClose}
                            >
                                <MenuItem style={{ fontWeight: 'bold', fontSize: 14, textTransform: 'capitalize' }} onClick={handleClose}>{userName}</MenuItem>
                                <Divider />
                                <MenuItem onClick={() => onLogout()} style={{ fontSize: 12 }}>Logout</MenuItem>
                            </Menu>
                        </div>
                    )}
                    {!isAuthorize && (
                        <div>
                            <Button
                                color="primary"
                                style={{
                                    marginBottom: 10,
                                    marginRight: 10,
                                    awidth: 150,
                                    textTransform: "capitalize",
                                    color: "#303F9F",
                                    borderColor: "#303F9F",
                                }}
                                onClick={() => window.location = "http://ucl.edu.pk/"}
                            >
                                <Link style={{ textDecoration: 'none' }} to='http://ucl.edu.pk/'>
                                    UCL Home
                                </Link>

                            </Button>
                        </div>
                    )}
                </Toolbar>
            </AppBar>
            <NotificationsBar
                setNotiDrawerOpen={(v) => setNotiDrawerOpen(v)}
                notiData={notiData}
                isNotiDrawerOpen={isNotiDrawerOpen}
            />
        </div>
    );
}
NavBar.propTypes = {
    logo: PropTypes.any,
    title: PropTypes.string,
    userName: PropTypes.string,
    isAuthorize: PropTypes.bool,
    isOpenMenu: PropTypes.bool,
    setOpenMenu: PropTypes.func
};

NavBar.defaultProps = {
    logo: null,
    title: "",
    userName: "Student",
    isAuthorize: false,
    isOpenMenu: false,
    setOpenMenu: fn => fn
};

export default NavBar;